import { ApiResponse } from '../types';
import { FulfilmentOptionsRequest, FulfilmentOptionsResponse, InternalFulfilmentV3OptionsResponse } from './types';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { generateRecaptchaHeaders } from '../utils/recaptcha/generateRecaptchaHeaders';
import { ApiClient } from '../client/ApiClient';

export const FULFILMENT_API_RECAPTCHA_ACTION = 'fulfilmentApi';

const hasApiError = (fulfilmentResponse: FulfilmentOptionsResponse | null) => {
  return !!fulfilmentResponse?.delivery?.presentation?.find(pres => pres?.type === 'Error');
};

export class FulfilmentClient extends ApiClient {
  private readonly _fulfillmentBaseUrl: string;
  private readonly _fulfillmentOptionsUrl: string;
  private readonly _fulfillmentMultipleOptionsUrl: string;
  private readonly _apiKey?: string;
  private readonly _countryCode: CountryCode;

  constructor({
    fulfillmentBaseUrl,
    fulfillmentOptionsUrl = '/FulfilmentOptions',
    fulfillmentMultipleOptionsUrl = '/FulfilmentOptionsCombined',
    apiKey,
    countryCode = 'AU',
  }: {
    fulfillmentBaseUrl: string;
    fulfillmentOptionsUrl?: string;
    fulfillmentMultipleOptionsUrl?: string;
    fulfillmentOptionsValidateUrl?: string;
    apiKey?: string;
    countryCode?: CountryCode;
  }) {
    super();
    this._fulfillmentBaseUrl = fulfillmentBaseUrl;
    this._fulfillmentOptionsUrl = fulfillmentOptionsUrl;
    this._fulfillmentMultipleOptionsUrl = fulfillmentMultipleOptionsUrl;
    this._apiKey = apiKey;
    this._countryCode = countryCode;
  }

  override get requestHeaders(): Record<string, string> {
    return {
      ...super.requestHeaders,
      ...getMultiTenancyHeaders(this._countryCode),
      ...(this._apiKey ? { 'Ocp-Apim-Subscription-Key': this._apiKey } : {}),
    };
  }

  getOptions = async (
    body: FulfilmentOptionsRequest,
    recaptchaToken: string
  ): Promise<ApiResponse<FulfilmentOptionsResponse>> => {
    const { data, statusCode, error } = await this._fetch<InternalFulfilmentV3OptionsResponse>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentOptionsUrl}`,
      method: 'POST',
      body,
      headers: { ...generateRecaptchaHeaders(recaptchaToken, FULFILMENT_API_RECAPTCHA_ACTION) },
    });

    if (error) {
      return { error, statusCode };
    }

    /* Checking for errors only in the delivery payload to see if the whole product has gone wrong
    /* but to avoid causing an issue where ClickAndCollect has an error but Delivery doesn't
    /* TODO: Move this error-handling down the chain to make it independent of fulfilment type */
    const wasApiError = Boolean(data?.delivery?.presentation?.find(pres => pres?.type === 'Error'));

    if (wasApiError) {
      return { error: { message: 'Internal API Error' }, statusCode: 500 };
    }

    const processedData: FulfilmentOptionsResponse = {
      clickAndCollect: data?.clickAndCollect,
      cashAndCarry: data?.cashAndCarry,
      delivery: data?.delivery as FulfilmentOptionsResponse['delivery'],
    };

    return { data: processedData, statusCode };
  };

  getMultipleOptions = async (
    body: Array<FulfilmentOptionsRequest | null>,
    recaptchaToken: string
  ): Promise<ApiResponse<Array<InternalFulfilmentV3OptionsResponse | null>>> => {
    const { data, statusCode, error } = await this._fetch<Array<FulfilmentOptionsResponse>>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentMultipleOptionsUrl}`,
      method: 'POST',
      body,
      headers: { ...generateRecaptchaHeaders(recaptchaToken, FULFILMENT_API_RECAPTCHA_ACTION) },
    });

    if (error) {
      return { error, statusCode };
    }

    /* Checking for errors only in the delivery payload to see if the whole product has gone wrong
    /* but to avoid causing an issue where ClickAndCollect has an error but Delivery doesn't
    /* TODO: Move this error-handling down the chain to make it independent of fulfilment type */
    const errorCheckedData = data?.map(d => (hasApiError(d) ? null : d));
    if (errorCheckedData?.every(d => d === null)) {
      return { error: { message: 'Internal API Error' }, statusCode: 500 };
    }

    return { data: errorCheckedData as Array<InternalFulfilmentV3OptionsResponse | null>, statusCode };
  };
}
